import React from 'react'
import Head from 'next/head'
import { getWidget, getFilterDataWidget, getFiltersListingPreview } from '@/helpers/WidgetHelper'
import {
  EntityDetails,
  LoyaltyCampaign,
  TemplateDefinition,
  TemplateWidget,
  WidgetCategoryLink,
  WidgetRegionLink,
} from '@/types'
import { LoyaltyCampaignContext } from '@/context/LoyaltyCampaignContext'
import { useContext } from 'react'
import { DefaultPropertiesContext } from '@/context/DefaultPropertiesContext'
import { getIndexTag } from '@/helpers/SeoHelper'
import { useRouter } from 'next/router'
import { AgentConfigurationResponse } from '@big-red-group/storefront-common-checkout'
import { RichSnippetContext } from '@/context/RichSnippetContext'
import { getFlattenedFaqs } from './HelpCentrePageTemplate'


type PlpPageTemplateProps = {
  entityType?: 'REGION' | 'CAMPAIGN' | 'CATEGORY' | 'SUBCATEGORY' | 'STATE' | 'THINGS_TO_DO' | 'HOME'
  agentConfiguration: AgentConfigurationResponse
  entityDetails: EntityDetails
  templateDefinition?: TemplateDefinition
  loyaltyCampaign?: LoyaltyCampaign
  canonicalLink?: string
}

const PlpPageTemplate: React.FC<PlpPageTemplateProps> = ({
  entityType,
  agentConfiguration,
  entityDetails,
  templateDefinition,
  loyaltyCampaign,
  canonicalLink,
}) => {
  const { id, title, heading, description, image, summary, products, name, urlSegment } = entityDetails //todo: add optional image to entity details(?)
  const { handlerKey } = agentConfiguration
  const router = useRouter()
  const defaultProperties = useContext(DefaultPropertiesContext)
  const isBrgAgent = defaultProperties?.agentConfiguration?.isBrgAgent
  const partnerCode = defaultProperties?.agentConfiguration?.partnerCode
  // Gets a region for region/category
  const region = (canonicalLink && canonicalLink?.split('/')[4]) || ''

  const widgets = templateDefinition?.widgets

  function getProductWidget(widget: TemplateWidget): TemplateWidget {
    if (entityType === 'THINGS_TO_DO' && widget.name === 'category-product-carousel') {
      widget.products = products
    }
    return widget
  }

  function getContextualCategories(): WidgetCategoryLink[] | undefined {
    let contextualCategories = undefined
    if (entityType === 'REGION') {
      const categoryLinks = widgets?.find(({ name }) => name === 'category-links')
      if (!categoryLinks) return

      contextualCategories = getFilterDataWidget(categoryLinks)
    } else if (entityType === 'CATEGORY') {
      if (!entityDetails?.subcategories) return

      contextualCategories = [
        ...entityDetails.subcategories.map((subcat) => ({ id: subcat.id, name: subcat.name, link: subcat.urlSegment })),
      ]
    } else if (entityType === 'THINGS_TO_DO') {
      const subcats = defaultProperties?.searchProperties?.categories.find((category) => category.id === id)
      if (!subcats?.subCategories) return

      contextualCategories = [
        ...subcats.subCategories.map((subcat) => ({ id: subcat.id, name: subcat.name, link: subcat.urlSegment })),
      ]
    }

    return contextualCategories
  }

  function getContextualRegions(): WidgetRegionLink[] | undefined {
    let contextualRegions = undefined

    if (entityType === 'THINGS_TO_DO') {
      const regionLinks = widgets?.find(({ name }) => name === 'region-links')
      if (!regionLinks) return

      contextualRegions = getFilterDataWidget(regionLinks)
    }

    return contextualRegions
  }

  const { collect } = RichSnippetContext.useRichSnippetStructuredData()
  if (entityType === 'HOME') {
   const { faqs } = getFlattenedFaqs(handlerKey)
    collect({
      type: 'FAQ',
      data: faqs,
    })
    collect({
      type: 'Organization',
      data: {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'ExperienceOz',
        url: 'https://www.experienceoz.com.au',
        telephone: '1300 791 793',
        email: 'info@experienceoz.com.au',
        sameAs: [
          'https://www.facebook.com/ExperienceOz',
          'https://www.youtube.com/user/experienceoz01',
          'https://www.instagram.com/experienceoz',
        ],
      },
    })
  }

  return (
    <>
      <Head>
        <meta name="pageid" content="plp" />
        <meta name="entityType" content={entityType} />
        <meta name="entityName" content={entityDetails?.name} />
        {entityType === 'HOME' && getIndexTag(isBrgAgent, 'Home', handlerKey, defaultProperties!)}
        {entityType !== 'HOME' && getIndexTag(isBrgAgent, 'plp', handlerKey, defaultProperties!)}
        {entityType !== 'HOME' && <title>{title}</title>}
        {canonicalLink && isBrgAgent && <link rel="canonical" href={canonicalLink} />}
        <meta name="description" property="og:description" content={entityDetails?.summary} />
      </Head>
      <LoyaltyCampaignContext.Provider value={loyaltyCampaign}>
        <div>
          {// when has templateDefinition has entityType. TODO improve ThingsTodoResponse type
          widgets?.map((widget: TemplateWidget, index: number) => (
            <React.Fragment key={index}>
              {widget.name === 'product-listing' && entityType !== 'HOME' ? (
                <div key={index}>
                  {getFiltersListingPreview(
                    getProductWidget(widget),
                    entityType!,
                    entityDetails,
                    getContextualRegions(),
                    getContextualCategories(),
                    handlerKey
                  )}
                </div>
              ) : (
                <div key={index}>
                  {getWidget(
                    getProductWidget(widget),
                    entityType!,
                    entityDetails,
                    defaultProperties?.topbarConfiguration!,
                    isBrgAgent,
                    handlerKey,
                    region,
                    partnerCode,
                    router.query
                  )}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </LoyaltyCampaignContext.Provider>
    </>
  )
}

export default PlpPageTemplate
